import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import { useTranslation } from "react-i18next";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import topicsIndex from "./data/index.json";
import {
  normalizeTopics,
  filterTopicsBySection,
  dedupeTopicsByUrlKeyPreferCustomer,
} from "./data/topicUtils";
import { getIconForTopic } from "./iconResolver";

const rawTopics = Array.isArray(topicsIndex) ? topicsIndex : [];
const normalizedTopics = normalizeTopics(rawTopics);

function getAllowedSections(userTyp, customerType) {
  const typ = Number(customerType);
  // Business Partner (2) sees the same FAQ as Service Partner (7): Service + For customers.
  if (typ === 3) return ["customer"];
  return ["service", "customer"];
}

const FAQList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = useJumboAuth();
  const authUser = auth?.getAuthUser?.() || {};
  const userTyp = authUser?.type ?? authUser?.typ;
  const isAdmin = userTyp === 1;
  const customerType =
    authUser?.customer?.customerType ?? authUser?.customer?.customer_type;
  const customerId =
    authUser?.customerId ??
    authUser?.customer?.customerId ??
    authUser?.customer?.id;
  // "Internal information" section is reserved for the main administrator only
  // (customerId 1 + user type 1).
  const isMainAdmin = Number(userTyp) === 1 && Number(customerId) === 1;
  const allowedSections = useMemo(() => {
    const base = getAllowedSections(userTyp, customerType);
    return isMainAdmin ? [...base, "internal"] : base;
  }, [userTyp, customerType, isMainAdmin]);
  const isCustomerType2Or3 =
    Number(customerType) === 2 || Number(customerType) === 3;

  const [searchQuery, setSearchQuery] = useState("");

  const visibleTopics = useMemo(() => {
    const filtered = normalizedTopics.filter((t) =>
      allowedSections.includes(t.section || "service")
    );
    const hasBothSections =
      allowedSections.includes("service") && allowedSections.includes("customer");
    if (hasBothSections) {
      return dedupeTopicsByUrlKeyPreferCustomer(filtered);
    }
    return filtered;
  }, [allowedSections]);

  const customerOnlyTopics = useMemo(
    () => filterTopicsBySection(normalizedTopics, "customer"),
    []
  );

  const showNotAvailable =
    isCustomerType2Or3 && customerOnlyTopics.length === 0;

  const filterBySearch = (topics, q) => {
    const trim = (q || "").trim().toLowerCase();
    if (!trim) return topics;
    return topics.filter((topic) => {
      const title = (topic.title || "").toLowerCase();
      const urlKey = (topic.urlKey || "").toLowerCase();
      const description = (topic.description || "").toLowerCase();
      return (
        title.includes(trim) || urlKey.includes(trim) || description.includes(trim)
      );
    });
  };

  const searchTrim = (searchQuery || "").trim();
  const serviceFiltered = useMemo(() => {
    const serviceTopics = visibleTopics.filter(
      (t) => (t.section || "service") === "service"
    );
    return filterBySearch(serviceTopics, searchQuery);
  }, [visibleTopics, searchQuery]);
  const customerFiltered = useMemo(() => {
    const customerTopics = visibleTopics.filter(
      (t) => (t.section || "service") === "customer"
    );
    return filterBySearch(customerTopics, searchQuery);
  }, [visibleTopics, searchQuery]);
  const internalFiltered = useMemo(() => {
    const internalTopics = visibleTopics.filter(
      (t) => (t.section || "service") === "internal"
    );
    return filterBySearch(internalTopics, searchQuery);
  }, [visibleTopics, searchQuery]);

  const hasAnyResults =
    serviceFiltered.length > 0 ||
    customerFiltered.length > 0 ||
    internalFiltered.length > 0;

  const renderTopicCard = (topic, idx) => {
    const Icon = getIconForTopic(topic);
    const handleClick = () => {
      navigate(`/dsok/faq/${topic.urlKey || ""}`);
    };
    return (
      <Grid item xs={12} sm={6} md={4} lg={4} key={`${topic.urlKey}-${topic.section}-${idx}`}>
        <Card
          variant="outlined"
          onClick={handleClick}
          sx={{
            height: "100%",
            cursor: "pointer",
            transition: "box-shadow 0.2s, background-color 0.2s",
            "&:hover": {
              boxShadow: 1,
              bgcolor: "action.hover",
            },
            "& .MuiCardContent-root": {
              pointerEvents: "auto",
            },
          }}
        >
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 48,
                  height: 48,
                  borderRadius: 1,
                  bgcolor: "action.hover",
                  flexShrink: 0,
                }}
              >
                {Icon && (
                  <Icon sx={{ fontSize: 24, color: "primary.main" }} />
                )}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h6" component="h2" gutterBottom>
                  {topic.title || topic.urlKey || "—"}
                </Typography>
                {topic.description && (
                  <Typography variant="body2" color="text.secondary">
                    {topic.description}
                  </Typography>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  return (
    <Box sx={{ px: 0 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: 2,
        }}
      >
        <Breadcrumbs
          aria-label="breadcrumb"
          sx={{ flex: "1 1 auto", minWidth: 0 }}
        >
          <Link to="/">{t("sidebar.menuItem.home")}</Link>
          {isAdmin && (
            <Link to="/dsok/customers">{t("sidebar.menuItem.customers")}</Link>
          )}
          <Typography color="text.primary">
            {t("faq.title", { defaultValue: "FAQ" })}
          </Typography>
        </Breadcrumbs>
      </Box>

      <Paper
        variant="outlined"
        sx={{
          bgcolor: "background.paper",
          p: 2,
          borderRadius: 1,
        }}
      >
        {showNotAvailable ? (
          <Typography color="text.secondary" sx={{ py: 3 }}>
            {t("faq.notAvailable", {
              defaultValue: "FAQ is currently not available.",
            })}
          </Typography>
        ) : (
          <>
            <TextField
              size="small"
              placeholder={t("faq.searchPlaceholder", {
                defaultValue: "Search topics…",
              })}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ mb: 2, minWidth: 220 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />

            {!hasAnyResults ? (
              <Typography color="text.secondary" sx={{ py: 3 }}>
                {searchTrim
                  ? t("faq.noResults", {
                      defaultValue: "No topics match your search.",
                    })
                  : t("faq.emptyState", { defaultValue: "No topics yet." })}
              </Typography>
            ) : (
              <>
                {internalFiltered.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle1"
                      component="h2"
                      fontWeight={600}
                      sx={{ mb: 2 }}
                    >
                      {t("faq.section.internal", {
                        defaultValue: "Interné informácie",
                      })}
                    </Typography>
                    <Grid
                      container
                      spacing={2}
                      alignItems="stretch"
                      sx={{ mb: 2 }}
                    >
                      {internalFiltered.map((topic, idx) =>
                        renderTopicCard(topic, idx)
                      )}
                    </Grid>
                  </Box>
                )}
                {serviceFiltered.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle1"
                      component="h2"
                      fontWeight={600}
                      sx={{ mb: 2 }}
                    >
                      {t("faq.section.service", { defaultValue: "Service" })}
                    </Typography>
                    <Grid
                      container
                      spacing={2}
                      alignItems="stretch"
                      sx={{ mb: 2 }}
                    >
                      <Grid item xs={12} sm={6} md={4} lg={4}>
                        <Card
                          variant="outlined"
                          onClick={() =>
                            navigate(
                              "/dsok/system-settings/developer/api-docs/partner"
                            )
                          }
                          sx={{
                            height: "100%",
                            cursor: "pointer",
                            transition:
                              "box-shadow 0.2s, background-color 0.2s",
                            "&:hover": {
                              boxShadow: 1,
                              bgcolor: "action.hover",
                            },
                          }}
                        >
                          <CardContent>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 2,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: 48,
                                  height: 48,
                                  borderRadius: 1,
                                  bgcolor: "action.hover",
                                  flexShrink: 0,
                                }}
                              >
                                <MenuBookOutlinedIcon
                                  sx={{ fontSize: 24, color: "primary.main" }}
                                />
                              </Box>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                  variant="h6"
                                  component="h3"
                                  gutterBottom
                                >
                                  {t("faq.apiDocs.title", {
                                    defaultValue: "API dokumentácia",
                                  })}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {t("faq.apiDocs.description", {
                                    defaultValue:
                                      "Partnerská API dokumentácia (externé objednávky, platby).",
                                  })}
                                </Typography>
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                      {serviceFiltered.map((topic, idx) =>
                        renderTopicCard(topic, idx)
                      )}
                    </Grid>
                  </Box>
                )}
                {customerFiltered.length > 0 && (
                  <Box>
                    <Typography
                      variant="subtitle1"
                      component="h2"
                      fontWeight={600}
                      sx={{ mb: 2 }}
                    >
                      {t("faq.section.customer", {
                        defaultValue: "For customers",
                      })}
                    </Typography>
                    <Grid container spacing={2} alignItems="stretch">
                      {customerFiltered.map((topic, idx) =>
                        renderTopicCard(topic, idx)
                      )}
                    </Grid>
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default FAQList;
